import { toast } from "@assetwatch/ui";
import {
  abortMotionAmplificationMultipartUpload,
  type MotionAmplificationJobId,
  reportMotionAmplificationJobFailure,
} from "../clients/motion-amplification";
import i18n from "../i18n/config";
import { removeMultipartFileHandle } from "./multipartUploadFileHandleStore";
import type { MultipartUploadSession } from "./multipartUploadSession";

const PAUSED_UPLOAD_TIMEOUT_MS = 60_000;
const pausedUploadTimeouts = new Map<
  MotionAmplificationJobId,
  ReturnType<typeof setTimeout>
>();

export function resetPausedMultipartUploadTimeoutsForTests(): void {
  for (const timeout of pausedUploadTimeouts.values()) {
    clearTimeout(timeout);
  }
  pausedUploadTimeouts.clear();
}

export async function expirePausedMultipartUpload(
  session: Pick<MultipartUploadSession, "jobId" | "sessionId">,
  refreshJobs: () => Promise<unknown>,
): Promise<void> {
  let abortError: unknown;
  try {
    await abortMotionAmplificationMultipartUpload(
      session.jobId,
      session.sessionId,
    );
  } catch (error) {
    abortError = error;
  }
  await removeMultipartFileHandle(session.jobId).catch(() => undefined);
  try {
    await reportMotionAmplificationJobFailure(session.jobId, {
      phase: "S3 upload",
      errorMessage: "Upload paused for one minute and expired.",
    });
  } finally {
    await refreshJobs();
  }
  if (abortError) throw abortError;
}

export function showPausedMultipartUploadToast({
  jobId,
  jobName,
  onResume,
  onExpire,
}: {
  jobId: MotionAmplificationJobId;
  jobName: string;
  onResume: () => void;
  onExpire: () => Promise<void>;
}) {
  if (pausedUploadTimeouts.has(jobId)) return;

  let expired = false;
  function expire() {
    if (expired) return;
    expired = true;
    clearTimeout(timeout);
    pausedUploadTimeouts.delete(jobId);
    void onExpire().catch(() => {
      toast.error(i18n.t("motionAmplification.uploadTimeoutFailed"), {
        timeout: 0,
      });
    });
  }

  const timeout = setTimeout(() => {
    expire();
  }, PAUSED_UPLOAD_TIMEOUT_MS);
  pausedUploadTimeouts.set(jobId, timeout);

  toast.error(i18n.t("motionAmplification.uploadPaused", { jobName }), {
    timeout: PAUSED_UPLOAD_TIMEOUT_MS,
    action: {
      label: i18n.t("motionAmplification.resumeUpload"),
      onClick: () => {
        if (expired) return;
        clearTimeout(timeout);
        pausedUploadTimeouts.delete(jobId);
        onResume();
      },
    },
  });
}
